import {
  type Category,
  type CategoryState,
  type CategorySubtype,
  type CategoryType,
  type EntityCategory,
  type EntityDepartment,
  type EntityLocation,
  type EntityOrganization,
  type EntityParty,
  type EntityUser,
  type ObjectIdentifier,
  ObjectKind,
} from "@digits-graphql/frontend/graphql-bearer"
import { v4 as generateUUID } from "uuid"

export const NEW_ENTITY_ID = "new-entity-id"

/*
 INTERFACES
*/

// Always: show new results in the typeahead
// Search: show new results only when searching
// Never: do not show new results
export type NewResultBehavior = "always" | "search" | "never"

export enum TypeaheadResultDecorator {
  CategoryDisabled = "CategoryDisabled",
  NewCategory = "NewCategory",
}

export type CategoryTypeaheadProps = {
  onResultSelected: OnSelectedCallback<CategoryTypeaheadResult>
  kind: ObjectKind.Category
  includedTypes?: CategoryType[]
  includedSubtypes?: CategorySubtype[]
  includedStates?: CategoryState[]
  suggestedCategoryIds?: string[]
  excludedCategoryIds?: string[]
  excludedAncestorIds?: string[]
  includedCategoryIds?: string[]
  enableSelectRootCategory?: boolean
  enableSelectParentCategory?: boolean
  allowAddNew?: boolean
  flatten?: boolean
}

export type PartyTypeaheadProps = {
  kind: ObjectKind.Party
  onResultSelected: OnSelectedCallback<PartyTypeaheadResult>
}

export type NewPartyTypeaheadProps = {
  kind: ObjectKind.Party
  allowAddNew: true
  newEntities?: NewPartyEntity[]
  onResultSelected: OnSelectedCallback<PartyWithNewEntitiesTypeaheadResult>
}

export type DepartmentTypeaheadProps = {
  kind: ObjectKind.Department
  onResultSelected: OnSelectedCallback<DepartmentTypeaheadResult>
}

export type LocationTypeaheadProps = {
  kind: ObjectKind.Location
  onResultSelected: OnSelectedCallback<LocationTypeaheadResult>
}

export type UserTypeaheadProps = {
  kind: ObjectKind.User
  objectIdentifier: ObjectIdentifier
  includeCurrentUser?: boolean
  onResultSelected: OnSelectedCallback<UserTypeaheadResult>
}

export type UserWithOrgTypeaheadProps = Omit<UserTypeaheadProps, "onResultSelected"> & {
  includeOrganization: true
  onResultSelected: OnSelectedCallback<UserWithOrgTypeaheadResult>
}

export type TypeaheadKinds =
  | CategoryTypeaheadProps
  | PartyTypeaheadProps
  | NewPartyTypeaheadProps
  | UserTypeaheadProps
  | UserWithOrgTypeaheadProps
  | DepartmentTypeaheadProps
  | LocationTypeaheadProps

export function isCategoryTypeAhead(t: TypeaheadKinds): t is CategoryTypeaheadProps {
  return t.kind === ObjectKind.Category
}

export function isPartyTypeAhead(t: TypeaheadKinds): t is PartyTypeaheadProps {
  return t.kind === ObjectKind.Party
}

export function isDepartmentTypeAhead(t: TypeaheadKinds): t is DepartmentTypeaheadProps {
  return t.kind === ObjectKind.Department
}

export function isLocationTypeAhead(t: TypeaheadKinds): t is LocationTypeaheadProps {
  return t.kind === ObjectKind.Location
}

export function isPartyWithNewTypeAhead(t: TypeaheadKinds): t is NewPartyTypeaheadProps {
  return isPartyTypeAhead(t) && (t as NewPartyTypeaheadProps).allowAddNew
}

export function isUserTypeAhead(t: TypeaheadKinds): t is UserTypeaheadProps {
  return t.kind === ObjectKind.User
}

export function isUserWithOrgTypeAhead(t: TypeaheadKinds): t is UserWithOrgTypeaheadProps {
  return isUserTypeAhead(t) && (t as UserWithOrgTypeaheadProps).includeOrganization
}

type TypeaheadBaseResult = {
  id: string
  depth?: number
  title: string
  subtitle?: string | null
  // A subtitle displayed only when there is a search term
  searchSubtitle?: string | null
  searchValues: string[]
  isSelectable: boolean
  isSuggestion?: boolean
  isNew?: boolean
  decorator?: TypeaheadResultDecorator
}

export type CategoryTypeaheadResult = TypeaheadBaseResult & {
  kind: ObjectKind.Category
  entity: EntityCategory
  // This is temporary until the EntityCategory and Category have consistent naming for this field
  displayNumber?: string
}

export type PartyTypeaheadResult = TypeaheadBaseResult & {
  kind: ObjectKind.Party
  entity: EntityParty
}

export type DepartmentTypeaheadResult = TypeaheadBaseResult & {
  kind: ObjectKind.Department
  entity: EntityDepartment
}

export type LocationTypeaheadResult = TypeaheadBaseResult & {
  kind: ObjectKind.Location
  entity: EntityLocation
}

export type PartyWithNewEntitiesTypeaheadResult = Omit<PartyTypeaheadResult, "entity"> & {
  entity: EntityParty | NewPartyEntity
}

export type CategoryWithNewEntitiesTypeaheadResult = Omit<CategoryTypeaheadResult, "entity"> & {
  entity: EntityCategory | NewCategoryEntity
}

export type UserTypeaheadResult = TypeaheadBaseResult & {
  kind: ObjectKind.User
  entity: EntityUser
}

export type UserWithOrgTypeaheadResult = Omit<UserTypeaheadResult, "entity"> & {
  entity: EntityUser | EntityOrganization
}

export type TypeaheadResult =
  | CategoryTypeaheadResult
  | CategoryWithNewEntitiesTypeaheadResult
  | PartyTypeaheadResult
  | PartyWithNewEntitiesTypeaheadResult
  | UserTypeaheadResult
  | UserWithOrgTypeaheadResult
  | DepartmentTypeaheadResult
  | LocationTypeaheadResult

export type OnSelectedCallback<T extends TypeaheadResult = TypeaheadResult> = (data: T) => void

export type NewPartyEntity = Pick<EntityParty, "id" | "name" | "iconUrl">
export type NewCategoryEntity = Pick<EntityCategory, "id" | "name" | "displayKey" | "active">
export type TypeaheadEntity =
  | EntityCategory
  | NewCategoryEntity
  | EntityParty
  | NewPartyEntity
  | EntityUser
  | EntityOrganization
  | EntityDepartment
  | EntityLocation

export function isTypeaheadParty(entity: TypeaheadEntity): entity is EntityParty {
  return !!(entity as EntityParty).designation
}

export function isTypeaheadNewParty(entity: TypeaheadEntity): entity is NewPartyEntity {
  return (
    !isTypeaheadParty(entity) &&
    !isTypeaheadCategory(entity) &&
    !isTypeaheadUser(entity) &&
    !isTypeaheadOrganization(entity)
  )
}

export function isTypeaheadCategory(entity: TypeaheadEntity): entity is EntityCategory {
  return !!(entity as EntityCategory).type
}

export function isCategory(entity?: Category | NewCategoryEntity): entity is Category {
  if (!entity) return false
  return !!(entity as EntityCategory).subtype
}

export function isTypeaheadUser(entity: TypeaheadEntity): entity is EntityUser {
  return !!(entity as EntityUser).emailAddress
}

export function isTypeaheadOrganization(entity: TypeaheadEntity): entity is EntityOrganization {
  return !!(entity as EntityOrganization).slug
}

export const newPartyEntityResultWithId = (name: string): PartyWithNewEntitiesTypeaheadResult => ({
  // Intentionally an invalid UUID, as this should be cleaned up before being sent to the BE
  id: NEW_ENTITY_ID + "-" + generateUUID(),
  title: name,
  searchValues: [name],
  isSelectable: true,
  kind: ObjectKind.Party,
  entity: {
    // Intentionally an invalid UUID, as this should be cleaned up before being sent to the BE
    id: NEW_ENTITY_ID + "-" + generateUUID(),
    name,
    iconUrl: null,
  },
})

export const newCategoryEntityResultWithId = (
  name: string
): CategoryWithNewEntitiesTypeaheadResult => ({
  // Intentionally an invalid UUID, as this should be cleaned up before being sent to the BE
  id: NEW_ENTITY_ID + "-" + generateUUID(),
  title: name,
  searchValues: [name],
  isSelectable: true,
  kind: ObjectKind.Category,
  entity: {
    // Intentionally an invalid UUID, as this should be cleaned up before being sent to the BE
    id: NEW_ENTITY_ID + "-" + generateUUID(),
    name,
    displayKey: "Tag01",
    active: true,
  },
})

export const isNewEntityId = (entityId?: string) => entityId?.startsWith(NEW_ENTITY_ID)
